<template>
  <button
    :type="action"
    :class="[type, { 'text-white': textWhite, new: isNew }]"
    :style="{ '--badge-text': `'${$t('buttons.new')}'` }"
    v-bind="$attrs"
  >
    <span>
      <slot></slot>
    </span>
  </button>
</template>
<script lang="ts">
import { defineComponent } from "vue"

export default defineComponent({
  name: "CustomButton",
  inheritAttrs: false,
  props: {
    type: {
      type: String as () =>
        | "outlined"
        | "primary"
        | "text"
        | "primary__white"
        | "outlined__white"
        | "empty",
      default: "primary",
    },
    action: {
      type: String as () => "submit" | "button",
      default: "button",
    },
    textWhite: {
      type: Boolean,
      default: false,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },
})
</script>

<style lang="scss" scoped>
button {
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 150px;
  font-family: Montserrat;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;

  &:disabled {
    cursor: unset;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 5px;
  }
  &.outlined,
  &.outlined__white {
    box-sizing: border-box;
    flex-direction: row;
    padding: 6px 10px;
    height: 32px;
    border-radius: 12px;
    position: relative;
    letter-spacing: -0.4px;
    background: none;
    &:not(&__white, .text-white) span {
      background: var(--text-gradient-165);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &__white:not(:hover) {
      &::after {
        background: white !important;
      }
    }

    &::after {
      pointer-events: none;
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 12px;
      padding: 1px;

      background: var(--text-gradient-165);
      -webkit-mask: linear-gradient(#000 0 0) content-box,
        linear-gradient(#000 0 0);
      mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }

    &:hover:not(:disabled) {
      span {
        background: var(--hover-gradient-165);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      &::after {
        background: var(--hover-gradient-165);
      }
    }
    &:disabled {
      opacity: 0.4;
      &::after {
        opacity: 0.4;
      }
    }
  }

  &.text {
    box-sizing: border-box;
    flex-direction: row;
    padding: 6px 10px;
    height: 32px;
    border-radius: 12px;
    position: relative;
    letter-spacing: -0.4px;
    min-width: max-content;
    background: none;
    span {
      background: var(--text-gradient-165);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &:hover:not(:disabled) {
      span {
        background: var(--hover-gradient-165);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }
    &:disabled {
      opacity: 0.4;
      &::after {
        opacity: 0.4;
      }
    }
  }

  &.empty {
    background: none;
  }

  &.primary,
  &.primary__white {
    padding: 16px 20px;
    height: 44px;
    background: var(--text-gradient-165);
    box-shadow: 0px 0px 1px rgba(69, 121, 245, 0.24),
      0px 2px 4px -1px rgba(27, 10, 82, 0.12),
      0px 16px 24px rgba(69, 121, 245, 0.24),
      0px 8px 8px -4px rgba(69, 121, 245, 0.12);
    border-radius: 16px;
    color: #ffffff;

    &:hover {
      background: var(--hover-gradient-165);
    }
    &:disabled {
      background: var(--text-gradient-165);
      opacity: 0.4;
      box-shadow: 0px 0px 1px rgba(69, 121, 245, 0.24),
        0px 2px 4px -1px rgba(27, 10, 82, 0.12),
        0px 16px 24px rgba(69, 121, 245, 0.24),
        0px 8px 8px -4px rgba(69, 121, 245, 0.12);
    }
    &__white {
      color: #212429;
      background: white;

      &:hover {
        background: white;
      }

      &:disabled {
        background: white;
        opacity: 0.4;
        box-shadow: 0px 0px 1px rgba(69, 121, 245, 0.24),
          0px 2px 4px -1px rgba(27, 10, 82, 0.12),
          0px 16px 24px rgba(69, 121, 245, 0.24),
          0px 8px 8px -4px rgba(69, 121, 245, 0.12);
      }
    }
  }

  &.new::before {
    content: var(--badge-text);
    height: 14px;
    position: absolute;
    top: -6px;
    right: -6px;
    background: #22c55e;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    padding: 2px 6px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    z-index: 2;
  }
}
</style>
